"use client";
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, ArrowRight, ArrowLeft, Shield, CheckCircle, KeyRound } from 'lucide-react';
import { useAuth } from './context/AuthContext';

// ─── Password Strength Bar ───────────────────────────────────────────────────

const STRENGTH_LEVELS = [
  { label: '',       color: '',            textColor: '' },
  { label: 'Weak',   color: 'bg-red-400',  textColor: 'text-red-500' },
  { label: 'Fair',   color: 'bg-orange-400', textColor: 'text-orange-500' },
  { label: 'Good',   color: 'bg-yellow-400', textColor: 'text-yellow-600' },
  { label: 'Strong', color: 'bg-teal-500', textColor: 'text-teal-600' },
];

const getPasswordStrength = (password) => {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8)          score++;
  if (/[A-Z]/.test(password))        score++;
  if (/[0-9]/.test(password))        score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

const PasswordStrengthBar = ({ password }) => {
  const score = getPasswordStrength(password);
  const { label, color, textColor } = STRENGTH_LEVELS[score];
  if (!password) return null;

  return (
    <div className="space-y-1.5 animate-fade-in">
      <div className="flex gap-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= score ? color : 'bg-gray-200'}`}
          />
        ))}
      </div>
      {label && <p className={`text-xs font-medium ${textColor}`}>{label} password</p>}
    </div>
  );
};

// ─── Password Requirements ───────────────────────────────────────────────────

const REQUIREMENTS = [
  { label: 'At least 8 characters',  test: (p) => p.length >= 8 },
  { label: 'One uppercase letter',    test: (p) => /[A-Z]/.test(p) },
  { label: 'One number',             test: (p) => /[0-9]/.test(p) },
  { label: 'One special character',  test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const RequirementItem = ({ label, met }) => (
  <div className="flex items-center gap-1.5">
    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${met ? 'bg-teal-500' : 'bg-gray-200'}`}>
      {met && (
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
    <span className={`text-xs transition-colors duration-200 ${met ? 'text-teal-700 font-medium' : 'text-gray-400'}`}>
      {label}
    </span>
  </div>
);

// ─── Password Input ──────────────────────────────────────────────────────────

const PasswordInput = ({ id, name, value, onChange, placeholder, disabled, error, show, onToggle }) => (
  <div className="relative">
    <input
      id={id}
      type={show ? 'text' : 'password'}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-4 py-3 pl-12 pr-12 bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
        error
          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
          : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
      }`}
    />
    <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${error ? 'text-red-400' : 'text-gray-400'}`} />
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
    >
      {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  </div>
);

// ─── Success State ───────────────────────────────────────────────────────────

const SuccessState = () => (
  <div className="flex flex-col items-center text-center space-y-6 animate-fade-in">
    <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center">
      <CheckCircle className="w-10 h-10 text-teal-600" />
    </div>
    <div className="space-y-2">
      <h2 className="text-lg font-semibold text-gray-800">All done!</h2>
      <p className="text-sm text-gray-600 leading-relaxed">
        Your password has been reset. Redirecting you to the login page…
      </p>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className="bg-gradient-to-r from-teal-500 to-teal-700 h-full rounded-full animate-progress" />
    </div>
    <Link
      to="/dashboard/login"
      className="w-full bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 transition-all duration-300 flex items-center justify-center gap-2"
    >
      Go to Login <ArrowRight className="w-5 h-5" />
    </Link>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();

  const email = location.state?.email || '';
  const otp   = location.state?.otp   || '';

  const [formData, setFormData]       = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [errors, setErrors]             = useState({});
  const [isLoading, setIsLoading]       = useState(false);
  const [isSuccess, setIsSuccess]       = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name] || errors.general) {
      setErrors((prev) => ({ ...prev, [name]: '', general: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.password)                          newErrors.password = 'Password is required';
    else if (formData.password.length < 8)           newErrors.password = 'Password must be at least 8 characters';
    if (!formData.confirmPassword)                   newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setErrors({});

    const result = await resetPassword(email, formData.password);

    if (!result.success) {
      setErrors({ general: result.message || 'Failed to reset password. Please try again.' });
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsSuccess(true);
    setTimeout(() => navigate('/dashboard/login'), 2500);
  };

  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-100 to-orange-50 flex items-center justify-center p-4 relative overflow-hidden">

      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-200 rounded-full opacity-20 animate-float" />
        <div className="absolute top-40 right-20 w-24 h-24 bg-orange-300 rounded-full opacity-30 animate-float-delayed" />
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-teal-100 rounded-full opacity-25 animate-float" />
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-orange-200 rounded-full opacity-20 animate-float-delayed" />
      </div>

      <div className="relative w-full max-w-md z-10">

        {/* Header */}
        <div className="text-center mb-8">
         <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl shadow-lg mb-4 transform hover:scale-105 transition-transform duration-300">
  <img
    src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
    alt="ManaPrint Logo"
    className="w-10 h-10 object-contain"
  />
</div>
          <h1 className="text-3xl font-bold text-teal-800 mb-2">
            {isSuccess ? 'Password Reset!' : 'Reset Password'}
          </h1>
          <p className="text-gray-600">
            {isSuccess
              ? 'Your password has been updated successfully'
              : 'Create a strong new password for your account'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white border-opacity-50 p-8 transform hover:scale-[1.02] transition-all duration-300">
          {isSuccess ? (
            <SuccessState />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm animate-shake">
                  {errors.general}
                </div>
              )}

              {/* New Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Lock className="w-4 h-4 text-teal-600" /> New Password
                </label>
                <PasswordInput
                  id="password" name="password"
                  value={formData.password} onChange={handleChange}
                  placeholder="Enter new password" disabled={isLoading}
                  error={errors.password} show={showPassword}
                  onToggle={() => setShowPassword((p) => !p)}
                />
                {errors.password && <p className="text-red-500 text-sm animate-fade-in">{errors.password}</p>}
                {formData.password && <PasswordStrengthBar password={formData.password} />}
              </div>

              {/* Requirements */}
              {formData.password && (
                <div className="grid grid-cols-2 gap-2 animate-fade-in">
                  {REQUIREMENTS.map((req) => (
                    <RequirementItem key={req.label} label={req.label} met={req.test(formData.password)} />
                  ))}
                </div>
              )}

              {/* Confirm Password */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Lock className="w-4 h-4 text-teal-600" /> Confirm Password
                </label>
                <PasswordInput
                  id="confirmPassword" name="confirmPassword"
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="Re-enter new password" disabled={isLoading}
                  error={errors.confirmPassword} show={showConfirm}
                  onToggle={() => setShowConfirm((p) => !p)}
                />
                {formData.confirmPassword && !errors.confirmPassword && (
                  <p className={`text-xs font-medium flex items-center gap-1 animate-fade-in ${passwordsMatch ? 'text-teal-600' : 'text-orange-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full inline-block ${passwordsMatch ? 'bg-teal-500' : 'bg-orange-400'}`} />
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                  </p>
                )}
                {errors.confirmPassword && <p className="text-red-500 text-sm animate-fade-in">{errors.confirmPassword}</p>}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Resetting Password…</>
                ) : (
                  <>Reset Password <ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              {/* Back */}
              <Link
                to="/dashboard/verify-otp"
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </Link>
            </form>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-4 p-4 bg-white bg-opacity-50 backdrop-blur-sm rounded-xl border border-white border-opacity-60">
          <div className="flex items-start gap-3">
            <Shield className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Keep it safe — </span>
              Never reuse old passwords. Use a unique password for your admin account.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float         { 0%,100% { transform: translateY(0); }    50% { transform: translateY(-20px); } }
        @keyframes float-delayed { 0%,100% { transform: translateY(0); }    50% { transform: translateY(-15px); } }
        @keyframes shake         { 0%,100% { transform: translateX(0); }    25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        @keyframes fade-in       { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes progress      { from { width:0%; } to { width:100%; } }
        .animate-float           { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed   { animation: float-delayed 8s ease-in-out infinite; }
        .animate-shake           { animation: shake 0.5s ease-in-out; }
        .animate-fade-in         { animation: fade-in 0.3s ease-out; }
        .animate-progress        { animation: progress 2.5s ease-in-out forwards; }
      `}</style>
    </div>
  );
};

export default ResetPassword;